import { Num as num } from '@nerdwallet/nw-client-lib';
import { CostSchedule, Outputs } from '../types';

export const BUYING_MORE_EXPENSIVE_TEXT =
  'Buying will never be cheaper than renting';
export const RENTING_MORE_EXPENSIVE_TEXT =
  'Renting will never be cheaper than buying';

const getNpvComparisonText = (
  rentingScenario: CostSchedule,
  buyingScenario: CostSchedule,
  breakevenYear: number | null,
  plannedStay: number
): string => {
  const rentingMonthlyCostNPV = rentingScenario.monthlyCostNPV;
  const buyingMonthlyCostNPV = buyingScenario.monthlyCostNPV;

  if (breakevenYear === null) {
    if (rentingMonthlyCostNPV < buyingMonthlyCostNPV) {
      return BUYING_MORE_EXPENSIVE_TEXT;
    }

    return RENTING_MORE_EXPENSIVE_TEXT;
  }

  if (rentingMonthlyCostNPV < buyingMonthlyCostNPV) {
    return `If you stay in your home for ${plannedStay} ${
      plannedStay < 2 ? 'year' : 'years'
    }, renting is cheaper than buying`;
  }
  if (rentingMonthlyCostNPV > buyingMonthlyCostNPV) {
    return `If you stay in your home for ${plannedStay} ${
      plannedStay < 2 ? 'year' : 'years'
    }, buying is cheaper than renting`;
  }

  return 'Buying and renting are about the same';
};

const getSavingText = (
  formattedMonthlyDiff: string,
  formattedBenefitNPV: string,
  breakevenYear: number | null
) => {
  return breakevenYear === null
    ? undefined
    : `You’ll save ${formattedMonthlyDiff} per month and ${formattedBenefitNPV} in total`;
};

export const getSummary = (
  outputs: Outputs,
  plannedStay: number
): {
  npvComparisonText: string;
  formattedMonthlyDiff: string;
  formattedBenefitNPV: string;
  savingText: string | undefined;
} => {
  const { rentingSchedule, buyingSchedule, breakevenYear } = outputs;
  const rentingScenario = rentingSchedule[plannedStay - 1];
  const buyingScenario = buyingSchedule[plannedStay - 1];

  const npvComparisonText = getNpvComparisonText(
    rentingScenario,
    buyingScenario,
    breakevenYear,
    plannedStay
  );

  const benefitNPV = rentingScenario.totalCostNPV - buyingScenario.totalCostNPV;
  const monthlyDiff =
    rentingScenario.monthlyCostNPV - buyingScenario.monthlyCostNPV;

  const formattedMonthlyDiff = num.formatCurrency(Math.abs(monthlyDiff));
  const formattedBenefitNPV = num.formatCurrency(Math.abs(benefitNPV), false);

  const savingText = getSavingText(
    formattedMonthlyDiff,
    formattedBenefitNPV,
    breakevenYear
  );

  return {
    npvComparisonText,
    formattedMonthlyDiff,
    formattedBenefitNPV,
    savingText,
  };
};
